export type PreviewInstrument = "piano" | "hold";

export type PreviewEnvelopePoint = { atSeconds: number; gainRatio: number };

export const PIANO_HARMONICS = [
  { ratio: 1, level: 0.72, detune: -1.5 },
  { ratio: 2, level: 0.2, detune: 1.2 },
  { ratio: 3, level: 0.08, detune: 3.5 },
] as const;

function appendPoint(points: PreviewEnvelopePoint[], atSeconds: number, gainRatio: number) {
  const time = Math.max(0, atSeconds);
  const previous = points.at(-1);
  if (previous && Math.abs(previous.atSeconds - time) < 0.00001) previous.gainRatio = gainRatio;
  else points.push({ atSeconds: time, gainRatio });
}

export function buildPreviewEnvelope(instrument: PreviewInstrument, durationSeconds: number): PreviewEnvelopePoint[] {
  const duration = Math.max(0.05, durationSeconds);
  const attack = Math.min(0.009, duration * 0.16);
  const release = Math.min(instrument === "piano" ? 0.07 : 0.045, duration * 0.3);
  const releaseStart = Math.max(attack, duration - release);
  const points: PreviewEnvelopePoint[] = [{ atSeconds: 0, gainRatio: 0.0001 }];
  appendPoint(points, attack, 1);
  if (instrument === "piano") {
    const earlyDecay = Math.min(releaseStart, Math.max(attack + 0.01, Math.min(0.42, duration * 0.32)));
    const tailRatio = Math.max(0.075, 0.34 * Math.exp(-Math.max(0, releaseStart - earlyDecay) / 2.8));
    appendPoint(points, earlyDecay, 0.34);
    appendPoint(points, releaseStart, tailRatio);
  } else {
    appendPoint(points, releaseStart, 0.74);
  }
  appendPoint(points, duration, 0.0001);
  return points;
}

export function playbackRange<T extends { startBeat: number; durationBeats: number }>(notes: T[], startBeat: number) {
  const audible = notes.filter((note) => note.startBeat + note.durationBeats > startBeat);
  const endBeat = audible.length ? Math.max(...audible.map((note) => note.startBeat + note.durationBeats)) : startBeat;
  return { audible, endBeat, remainingBeats: Math.max(0, endBeat - startBeat) };
}
